/**
 * Route Store (Zustand)
 *
 * @deprecated This store mixes data cache with UI state.
 * Migrate to the new architecture:
 *
 * **Data fetching/mutations** → Use TanStack Query hooks:
 * ```tsx
 * import { useRoutes, useCalculateRoute } from '@features/routes/queries';
 * ```
 *
 * **UI-only state** → Use existing route hooks:
 * ```tsx
 * import { useRouteActions } from '@features/routes/hooks';
 * ```
 *
 * This store will be removed in a future version.
 * See: apps/web/docs/ARCHITECTURE_MIGRATION_PLAN.md
 *
 * ---
 * Legacy: Global state management for route planning and calculation.
 * Handles multi-week/multi-day route optimization with clustering.
 */

import { create } from 'zustand';
import * as routeService from '../api/routeApi';
import { setActiveRoute as setActiveRouteApi, getActiveRoute as getActiveRouteApi } from '../api/routeApi';
import { toast } from '@/shared/utils/notifications';
import { logger } from '@/shared/utils/logger';
import type {
  RouteParams,
  RouteData,
  RouteHistory,
  RouteStopEnhanced,
  DayRouteData,
} from '@shared/types/api';

/**
 * Calculation phases for better progress insight
 *
 * NOTE: Geocoding phase removed - addresses are now geocoded in Address Management
 * Route calculation now runs much faster (~2 minutes instead of 4+ minutes)
 */
type CalculationPhase =
  | 'idle'
  | 'sending_request'
  | 'loading_campaign'
  | 'calculating_routes'
  | 'optimizing'
  | 'complete';

const PHASE_MESSAGES: Record<CalculationPhase, string> = {
  idle: 'Ready',
  sending_request: 'Sending request to server...',
  loading_campaign: 'Loading campaign organizations...',
  calculating_routes: 'Calculating optimal routes with OSRM...',
  optimizing: 'Optimizing route order and clustering...',
  complete: 'Route calculation complete!',
};

/**
 * Route store state and actions
 */
interface RouteStore {
  // State
  routes: RouteData | null;
  routeHistory: RouteHistory[];
  isCalculating: boolean;
  calculationProgress: number;
  calculationPhase: CalculationPhase;
  calculationElapsedSeconds: number;
  selectedRouteId: string | null;
  activeRouteId: string | null;  // Persisted active route (for Review Routes parity)
  activeRoutesByCampaign: Record<string, string>;  // Campaign-specific active routes
  isSettingActiveRoute: boolean;
  isLoadingHistory: boolean;
  error: string | null;

  // Route action state
  isPerformingAction: boolean;
  actionError: string | null;

  // Actions
  calculateRoute: (params: RouteParams) => Promise<void>;
  fetchRouteHistory: (campaignId?: string) => Promise<void>;
  fetchRouteStops: (routeId: string) => Promise<void>;
  loadRoute: (routeId: string) => Promise<void>;
  exportRoute: (routeId: string, format: 'excel' | 'kml') => Promise<void>;
  setSelectedRoute: (routeId: string | null) => void;
  setActiveRoute: (campaignId: string, routeId: string) => Promise<void>;  // Persist active route
  fetchActiveRoute: (campaignId: string) => Promise<string | null>;  // Load active route from backend, returns route ID
  deleteRouteFromHistory: (routeId: string) => Promise<void>;
  markOrganizationVisited: (campaignId: string, orgId: number, visited: boolean, outcome?: string, notes?: string) => Promise<void>;
  updateRouteNameInHistory: (routeId: string, newName: string) => void;  // Update route name in local state
  clearError: () => void;
  reset: () => void;

  // Route actions (Phase 2)
  removeStopFromRoute: (routeId: string, orgId: number) => Promise<void>;
  moveToMailers: (routeId: string, orgId: number) => Promise<void>;
  deleteFromCampaign: (campaignId: string, orgId: number) => Promise<void>;
  clearActionError: () => void;
}

/**
 * Initial state
 */
const initialState = {
  routes: null,
  routeHistory: [],
  isCalculating: false,
  calculationProgress: 0,
  calculationPhase: 'idle' as CalculationPhase,
  calculationElapsedSeconds: 0,
  selectedRouteId: null,
  activeRouteId: null,
  activeRoutesByCampaign: {} as Record<string, string>,
  isSettingActiveRoute: false,
  isLoadingHistory: false,
  error: null,
  isPerformingAction: false,
  actionError: null,
};

/**
 * Create route store
 */
export const useRouteStore = create<RouteStore>((set, get) => ({
  ...initialState,

  /**
   * Calculate multi-week route with clustering
   *
   * @param params - Route calculation parameters
   * @example
   * ```tsx
   * const { calculateRoute } = useRouteStore();
   * await calculateRoute({
   *   campaign_id: 'CAMPAIGN_123',
   *   start_location: 'Houston, TX',
   *   end_location: 'Houston, TX',
   *   weeks: 12,
   *   days_per_week: 5,
   *   clustering_enabled: true,
   *   distance_filter: 50
   * });
   * ```
   */
  calculateRoute: async (params: RouteParams) => {
    const startTime = performance.now();

    logger.log('%c[RouteStore] ========== ROUTE CALCULATION STARTED ==========', 'color: #4CAF50; font-weight: bold');
    logger.log('[RouteStore] Timestamp:', new Date().toISOString());
    logger.log('[RouteStore] Campaign ID:', params.campaign_id);
    logger.log('[RouteStore] Parameters:', {
      weeks: params.weeks,
      days_per_week: params.days_per_week,
      max_stops_per_day: params.max_stops_per_day,
      distance_filter: params.distance_filter,
      clustering_enabled: params.clustering_enabled,
      use_2opt: params.use_2opt_optimization,
    });
    logger.log('[RouteStore] Start Location:', params.start_location);
    logger.log('[RouteStore] End Location:', params.end_location);

    // Helper to update phase with logging
    const setPhase = (phase: CalculationPhase, progress: number) => {
      const elapsed = Math.round((performance.now() - startTime) / 1000);
      const message = PHASE_MESSAGES[phase];
      logger.log(`%c[RouteStore] Phase: ${phase} (${elapsed}s elapsed) - ${message}`, 'color: #2196F3');
      set({
        calculationPhase: phase,
        calculationProgress: progress,
        calculationElapsedSeconds: elapsed,
      });
    };

    set({
      isCalculating: true,
      calculationProgress: 0,
      calculationPhase: 'sending_request',
      calculationElapsedSeconds: 0,
      error: null,
    });

    // Timer to update elapsed time and estimate phase
    const timerInterval = setInterval(() => {
      const elapsed = Math.round((performance.now() - startTime) / 1000);
      const currentState = get();

      // Estimate phase based on elapsed time (backend doesn't send progress)
      // NEW TIMING (geocoding removed): 0-2s loading, 2-60s routing, 60-120s optimization
      // Much faster than before (was 4+ minutes, now ~2 minutes)
      let estimatedPhase: CalculationPhase = currentState.calculationPhase;
      let estimatedProgress = currentState.calculationProgress;

      if (elapsed < 2) {
        estimatedPhase = 'loading_campaign';
        estimatedProgress = 10;
      } else if (elapsed < 60) {
        estimatedPhase = 'calculating_routes';
        // Progress from 20% to 70% during routing (OSRM phase)
        estimatedProgress = Math.min(20 + Math.floor((elapsed - 2) * 0.86), 70);
      } else if (elapsed < 120) {
        estimatedPhase = 'optimizing';
        // Progress from 70% to 95% during optimization
        estimatedProgress = Math.min(70 + Math.floor((elapsed - 60) * 0.42), 95);
      } else {
        // Still optimizing after 2 minutes (for very large campaigns)
        estimatedPhase = 'optimizing';
        estimatedProgress = 95;
      }

      // Only log phase changes
      if (estimatedPhase !== currentState.calculationPhase) {
        logger.log(`%c[RouteStore] Phase: ${estimatedPhase} (${elapsed}s) - ${PHASE_MESSAGES[estimatedPhase]}`, 'color: #2196F3');
      }

      // Log every 10 seconds with elapsed time
      if (elapsed % 10 === 0 && elapsed > 0) {
      }

      set({
        calculationPhase: estimatedPhase,
        calculationProgress: estimatedProgress,
        calculationElapsedSeconds: elapsed,
      });
    }, 1000);

    try {
      logger.log('[RouteStore] Sending API request...');

      const result = await routeService.calculateRoute(params);

      logger.log('%c[RouteStore] API response received successfully!', 'color: #4CAF50');

      // Clear timer
      clearInterval(timerInterval);

      const endTime = performance.now();
      const totalSeconds = Math.round((endTime - startTime) / 1000);

      setPhase('complete', 100);

      // Refresh history FIRST so the new route appears in the list
      await get().fetchRouteHistory(params.campaign_id);

      // THEN set the route data and auto-select the new route
      // This ensures the new route is both highlighted in history AND displayed
      set({
        routes: result,
        selectedRouteId: result.route_id,
        calculationProgress: 100,
        calculationPhase: 'complete',
        isCalculating: false,
      });

      // Log result summary
      logger.log(`[RouteStore] Summary:`, {
        total_stops: result.total_stops,
        total_weeks: result.total_weeks,
        total_distance_miles: result.total_distance_miles?.toFixed(1),
        total_time_hours: result.total_time_minutes ? Math.round(result.total_time_minutes / 60) : 'N/A',
        weeks_generated: result.weeks?.length || 0,
      });

      if (result.weeks?.length > 0) {
        result.weeks.forEach((week) => {
        });
      }

      logger.log(`[RouteStore] Auto-selected new route: ${result.route_id}`);
    } catch (error) {
      clearInterval(timerInterval);

      const elapsed = Math.round((performance.now() - startTime) / 1000);
      const err = error as Error;

      logger.error('%c[RouteStore] ========== ROUTE CALCULATION FAILED ==========', 'color: #F44336; font-weight: bold');
      logger.error(`[RouteStore] Failed after ${elapsed}s`);
      logger.error('[RouteStore] Error Name:', err.name);
      logger.error('[RouteStore] Error Message:', err.message);
      logger.error('[RouteStore] Error Stack:', err.stack);

      // Check for common error patterns
      const errorMessage = err.message || 'Failed to calculate route';
      let userFriendlyError = errorMessage;

      if (errorMessage.includes('timeout') || errorMessage.includes('Timeout') || errorMessage.includes('ETIMEDOUT')) {
        userFriendlyError = 'Route calculation timed out. Try reducing the number of weeks or locations per day.';
        logger.error('[RouteStore] Cause: Request timeout');
      } else if (errorMessage.includes('geocod') || errorMessage.includes('address')) {
        userFriendlyError = `Address error: ${errorMessage}. Please verify addresses in Address Management first.`;
        logger.error('[RouteStore] Cause: Geocoding/address issue');
      } else if (errorMessage.includes('network') || errorMessage.includes('Network') || errorMessage.includes('fetch')) {
        userFriendlyError = 'Network error. Please check your connection and try again.';
        logger.error('[RouteStore] Cause: Network issue');
      } else if (errorMessage.includes('500') || errorMessage.includes('Internal')) {
        userFriendlyError = 'Server error during route calculation. Please try again in a few minutes.';
        logger.error('[RouteStore] Cause: Backend server error');
      }

      set({
        error: userFriendlyError,
        isCalculating: false,
        calculationProgress: 0,
        calculationPhase: 'idle',
      });
      throw error;
    }
  },

  /**
   * Fetch route history
   *
   * @param campaignId - Optional campaign ID filter
   * @example
   * ```tsx
   * const { fetchRouteHistory } = useRouteStore();
   * await fetchRouteHistory('CAMPAIGN_123');
   * ```
   */
  fetchRouteHistory: async (campaignId?: string) => {
    set({ isLoadingHistory: true, error: null });

    try {
      const history = await routeService.fetchRouteHistory(campaignId);

      set({
        routeHistory: history,
        isLoadingHistory: false,
      });

    } catch (error) {
      logger.error('[RouteStore] Failed to fetch route history:', error);
      set({
        error: (error as Error).message || 'Failed to fetch route history',
        isLoadingHistory: false,
      });
      throw error;
    }
  },

  /**
   * Fetch route stops for a specific route
   *
   * Note: This requires backend implementation to store full route data
   *
   * @param routeId - Route ID from history
   * @example
   * ```tsx
   * const { fetchRouteStops } = useRouteStore();
   * await fetchRouteStops('ROUTE_20251120_123456');
   * ```
   */
  fetchRouteStops: async (routeId: string) => {
    set({ error: null });

    try {
      // This would load the full route data from backend
      // For now, we'll need to recalculate or store it in history
      logger.warn('[RouteStore] fetchRouteStops not yet implemented - backend needs full route storage');

      // Placeholder: Set selected route from history
      const historyItem = get().routeHistory.find(h => h.route_id === routeId);
      if (historyItem) {
        set({ selectedRouteId: routeId });
      }
    } catch (error) {
      logger.error('[RouteStore] Failed to fetch route stops:', error);
      set({
        error: (error as Error).message || 'Failed to fetch route stops',
      });
      throw error;
    }
  },

  /**
   * Load full route data by ID (from history)
   *
   * This fetches the complete route calculation result from the backend,
   * including all weeks, days, and stops.
   *
   * @param routeId - Route ID from history
   * @example
   * ```tsx
   * const { loadRoute } = useRouteStore();
   * await loadRoute('ROUTE_20251120_123456');
   * ```
   */
  loadRoute: async (routeId: string) => {
    set({ error: null, isCalculating: true, calculationProgress: 0 });

    try {
      const routeData = await routeService.fetchRouteData(routeId);

      set({
        routes: routeData,
        selectedRouteId: routeId,
        isCalculating: false,
        calculationProgress: 100,
      });

      logger.log('[RouteStore] Route loaded successfully:', {
        route_id: routeData.route_id,
        total_stops: routeData.total_stops,
        total_weeks: routeData.total_weeks,
      });

      // Toast silenced - route loading is a background operation
    } catch (error) {
      const errorMessage = (error as Error).message || 'Failed to load route';
      logger.error('[RouteStore] Failed to load route:', error);

      // Check if this is a 404 error (route not found)
      const is404 = errorMessage.includes('not found') || errorMessage.includes('404');

      if (is404) {
        logger.warn('[RouteStore] Route not found, clearing stale active route reference:', routeId);

        // Clear stale active route references
        set((state) => {
          const updatedActiveRoutes = { ...state.activeRoutesByCampaign };

          // Find and remove the stale route from campaign-specific active routes
          Object.keys(updatedActiveRoutes).forEach((campaignId) => {
            if (updatedActiveRoutes[campaignId] === routeId) {
              delete updatedActiveRoutes[campaignId];
            }
          });

          return {
            error: null, // Don't show error for missing routes (graceful degradation)
            isCalculating: false,
            calculationProgress: 0,
            activeRouteId: state.activeRouteId === routeId ? null : state.activeRouteId,
            activeRoutesByCampaign: updatedActiveRoutes,
            selectedRouteId: state.selectedRouteId === routeId ? null : state.selectedRouteId,
          };
        });

        // Show info message instead of error
        // Don't show toast - silent cleanup for better UX
      } else {
        // For other errors (network, server, etc.), show error normally
        set({
          error: errorMessage,
          isCalculating: false,
          calculationProgress: 0,
        });
        toast.error('Failed to load route');
      }

      throw error;
    }
  },

  /**
   * Export route to Excel or KML
   *
   * @param routeId - Route ID from history
   * @param format - Export format ('excel' or 'kml')
   * @example
   * ```tsx
   * const { exportRoute } = useRouteStore();
   * await exportRoute('ROUTE_20251120_123456', 'excel');
   * ```
   */
  exportRoute: async (routeId: string, format: 'excel' | 'kml') => {
    set({ error: null });

    try {
      const downloadUrl = await routeService.exportRoute(routeId, format);

      // Trigger download
      window.open(downloadUrl, '_blank');

    } catch (error) {
      logger.error('[RouteStore] Failed to export route:', error);
      set({
        error: (error as Error).message || 'Failed to export route',
      });
      throw error;
    }
  },

  /**
   * Set selected route (for viewing details)
   *
   * @param routeId - Route ID or null to clear
   */
  setSelectedRoute: (routeId: string | null) => {
    set({ selectedRouteId: routeId });
  },

  /**
   * Set active route (persisted to backend)
   *
   * This is the route that will be used for Review Routes and Execution Tracking.
   * Unlike setSelectedRoute (view only), this persists to the backend.
   *
   * @param campaignId - Campaign ID
   * @param routeId - Route ID to set as active
   * @example
   * ```tsx
   * const { setActiveRoute } = useRouteStore();
   * await setActiveRoute('CAMPAIGN_123', 'ROUTE_20251120_123456');
   * ```
   */
  setActiveRoute: async (campaignId: string, routeId: string) => {
    set({ isSettingActiveRoute: true, error: null });

    try {
      await setActiveRouteApi(campaignId, routeId);

      set((state) => ({
        activeRouteId: routeId,
        selectedRouteId: routeId,  // Also select it for viewing
        activeRoutesByCampaign: {
          ...state.activeRoutesByCampaign,
          [campaignId]: routeId,  // Store campaign-specific active route
        },
        isSettingActiveRoute: false,
      }));

      toast.success('Route set as active');

      // Load the full route data
      await get().loadRoute(routeId);
    } catch (error) {
      logger.error('[RouteStore] Failed to set active route:', error);
      const errorMessage = (error as Error).message || 'Failed to set active route';
      set({ error: errorMessage, isSettingActiveRoute: false });
      toast.error(errorMessage);
      throw error;
    }
  },

  /**
   * Fetch active route from backend
   *
   * @param campaignId - Campaign ID
   * @returns The active route ID if found, null otherwise
   */
  fetchActiveRoute: async (campaignId: string): Promise<string | null> => {
    try {
      const activeRouteId = await getActiveRouteApi(campaignId);

      set((state) => {
        const newState = {
          activeRouteId,
          activeRoutesByCampaign: {
            ...state.activeRoutesByCampaign,
            [campaignId]: activeRouteId || '',  // Store campaign-specific active route
          },
        };
        return newState;
      });

      return activeRouteId;
    } catch (error) {
      logger.error('[RouteStore] Failed to fetch active route:', error);
      // Don't throw - active route is optional
      return null;
    }
  },

  /**
   * Delete route from history
   *
   * @param routeId - Route ID to delete
   * @example
   * ```tsx
   * const { deleteRouteFromHistory } = useRouteStore();
   * await deleteRouteFromHistory('ROUTE_20251120_123456');
   * ```
   */
  deleteRouteFromHistory: async (routeId: string) => {
    set({ error: null });

    try {
      // Call backend DELETE endpoint
      await routeService.deleteRouteFromHistory(routeId);

      // Remove from local state
      set((state) => ({
        routeHistory: state.routeHistory.filter(h => h.route_id !== routeId),
        selectedRouteId: state.selectedRouteId === routeId ? null : state.selectedRouteId,
      }));

      toast.success('Route deleted successfully');
    } catch (error) {
      logger.error('[RouteStore] Failed to delete route:', error);
      const errorMessage = (error as Error).message || 'Failed to delete route';
      set({ error: errorMessage });
      toast.error(errorMessage);
      throw error;
    }
  },

  /**
   * Update route name in local state (after successful API update)
   *
   * @param routeId - Route ID
   * @param newName - New route name
   */
  updateRouteNameInHistory: (routeId: string, newName: string) => {
    set((state) => ({
      routeHistory: state.routeHistory.map((route) =>
        route.route_id === routeId
          ? { ...route, route_name: newName }
          : route
      ),
      // Also update the loaded route if it matches
      routes: state.routes?.route_id === routeId
        ? { ...state.routes, route_name: newName }
        : state.routes,
    }));
  },

  /**
   * Clear error state
   */
  clearError: () => {
    set({ error: null });
  },

  /**
   * Mark an organization as visited
   *
   * @param campaignId - Campaign ID
   * @param orgId - Organization ID
   * @param visited - Whether the organization was visited
   * @param outcome - Optional outcome/notes
   * @param notes - Optional additional notes
   */
  markOrganizationVisited: async (
    campaignId: string,
    orgId: number,
    visited: boolean,
    outcome?: string,
    notes?: string
  ) => {
    try {
      await routeService.markOrganizationVisited(campaignId, orgId, {
        visited,
        outcome,
        notes,
      });

      toast.success(visited ? 'Organization marked as visited' : 'Visit status cleared');
    } catch (error) {
      logger.error('[RouteStore] Failed to mark organization visited:', error);
      set({
        error: (error as Error).message || 'Failed to update visit status',
      });
      throw error;
    }
  },

  /**
   * Reset store to initial state
   */
  reset: () => {
    set(initialState);
  },

  /**
   * Remove organization from route (Phase 2)
   *
   * @param routeId - Route ID
   * @param orgId - Organization ID
   */
  removeStopFromRoute: async (routeId: string, orgId: number) => {
    set({ isPerformingAction: true, actionError: null });

    try {
      // Import routeActionsApi dynamically to avoid circular dependencies
      const { removeFromRoute } = await import('../api/routeActionsApi');
      await removeFromRoute(routeId, orgId);

      set({ isPerformingAction: false });

      // Refresh route data
      await get().loadRoute(routeId);

    } catch (error) {
      logger.error('[RouteStore] Failed to remove stop from route:', error);
      const errorMessage = (error as Error).message || 'Failed to remove stop from route';
      set({ actionError: errorMessage, isPerformingAction: false });
      throw error;
    }
  },

  /**
   * Move organization to mailers (Phase 2)
   *
   * @param routeId - Route ID
   * @param orgId - Organization ID
   */
  moveToMailers: async (routeId: string, orgId: number) => {
    set({ isPerformingAction: true, actionError: null });

    try {
      // Import routeActionsApi dynamically to avoid circular dependencies
      const { moveToMailers } = await import('../api/routeActionsApi');
      await moveToMailers(routeId, orgId);

      set({ isPerformingAction: false });

      // Refresh route data
      await get().loadRoute(routeId);

    } catch (error) {
      logger.error('[RouteStore] Failed to move to mailers:', error);
      const errorMessage = (error as Error).message || 'Failed to move to mailers';
      set({ actionError: errorMessage, isPerformingAction: false });
      throw error;
    }
  },

  /**
   * Delete organization from campaign (Phase 2)
   *
   * @param campaignId - Campaign ID
   * @param orgId - Organization ID
   */
  deleteFromCampaign: async (campaignId: string, orgId: number) => {
    set({ isPerformingAction: true, actionError: null });

    try {
      // Import routeActionsApi dynamically to avoid circular dependencies
      const { deleteFromCampaign } = await import('../api/routeActionsApi');
      await deleteFromCampaign(campaignId, orgId);

      set({ isPerformingAction: false });

      // Refresh route history to reflect the change
      await get().fetchRouteHistory(campaignId);

    } catch (error) {
      logger.error('[RouteStore] Failed to delete from campaign:', error);
      const errorMessage = (error as Error).message || 'Failed to delete from campaign';
      set({ actionError: errorMessage, isPerformingAction: false });
      throw error;
    }
  },

  /**
   * Clear action error state
   */
  clearActionError: () => {
    set({ actionError: null });
  },
}));
